"use client";

import { useRouter } from "next/navigation";
import { useSession } from "next-auth/react";
import { api } from "~/trpc/react";
import { 
  StatCard, 
  ActionButton, 
  SectionCard, 
} from "~/components/ui";
import {
  ManageActionsIcon,
  FilePlusIcon,
  UploadIcon,
  EditIcon,
  GlobeIcon,
  SearchIcon,
} from "~/components/icons";

export function WriteOnlyDashboardContent() {
  const router = useRouter();
  const { data: session } = useSession();

  const userId = session?.user?.id ? parseInt(session.user.id) : null;
  
  const { data: userActivity, isLoading: isLoadingActivity } = api.accounts.getUserActivity.useQuery(
    { id: userId ?? 0 },
    { enabled: !!userId }
  );

  const formatNumber = (num: number) => {
    return num.toLocaleString();
  };

  const formatDate = (date: Date | string | null | undefined) => {
    if (!date) return "—";
    return new Intl.DateTimeFormat("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    }).format(new Date(date));
  };

  const lastSearchDate = userActivity?.lastSearchDate ?? null;
  const lastUpdatedDate = userActivity?.lastUpdatedDate ?? null;
  const totalRecords = userActivity?.totalRecords ?? 0;

  return (
    <div className="flex flex-col gap-4 sm:gap-6 px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8">
      {/* Stats Row */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
        <StatCard
          label="Last Search"
          value={isLoadingActivity ? "..." : formatDate(lastSearchDate)}
          icon={<SearchIcon className="h-5 w-5 text-[#003261]" />}
        />
        <StatCard
          label="Last Updated"
          value={isLoadingActivity ? "..." : formatDate(lastUpdatedDate)}
          icon={<EditIcon className="h-5 w-5 text-[#003261]" />}
        />
        <StatCard
          label="Total Records"
          value={isLoadingActivity ? "..." : formatNumber(totalRecords)}
          icon={<GlobeIcon className="h-5 w-5 text-[#003261]" />}
        />
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {/* Submit Section */}
        <SectionCard 
          title="Submit" 
          className="w-full"
        >
          <div className="flex flex-col">
            <ActionButton
              title="Manage Actions"
              description="Filter and review actions"
              icon={<ManageActionsIcon className="h-4 w-4 text-[#003261]" />}
              onClick={() => router.push('/submissions/manage')}
            />
            <ActionButton
              title="Add Action"
              description="Add an action to the DADB database"
              icon={<FilePlusIcon className="h-4 w-4 text-[#003261]" />}
              onClick={() => router.push('/submissions/add')}
            />
            <ActionButton
              title="Bulk Add with File"
              description="Add multiple actions at once"
              icon={<UploadIcon className="h-4 w-4 text-[#003261]" />}
              onClick={() => router.push('/submissions/bulk')}
            />
          </div>
        </SectionCard>

        {/* Account Settings Section */}
        <SectionCard 
          title="Account Settings" 
          className="w-full"
        >
          <div className="flex flex-col">
            <ActionButton
              title="Edit Account"
              description="Make changes to your account information."
              icon={<EditIcon className="h-4 w-4 text-[#003261]" />}
              onClick={() => router.push('/settings')}
            />
            <ActionButton
              title="Change password"
              description="Update your password securely."
              icon={<FilePlusIcon className="h-4 w-4 text-[#003261]" />}
              onClick={() => router.push('/settings/change-password')}
            />
          </div>
        </SectionCard>
      </div>
    </div>
  );
}

